
import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useMaintenanceMode } from "@/hooks/useMaintenanceMode";
import MaintenanceScreen from "@/components/MaintenanceScreen";
import Layout from "./components/Layout";
import { AuthProvider } from "./hooks/useAuth";
import AdminLayout from "./components/AdminLayout";
import ProtectedRoute from "./components/ProtectedRoute";

// Critical pages - load immediately
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";

// Lazy load all other pages to reduce initial bundle size
const About = lazy(() => import("./pages/About"));
const Training = lazy(() => import("./pages/Training"));
const Tournaments = lazy(() => import("./pages/Tournaments"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Events = lazy(() => import("./pages/Events"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Contact = lazy(() => import("./pages/Contact"));
const Auth = lazy(() => import("./pages/Auth"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const AGB = lazy(() => import("./pages/AGB"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Search = lazy(() => import("./pages/Search"));

// Admin pages - lazy load for better performance
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const BlogManagement = lazy(() => import("./pages/admin/BlogManagement"));
const MediaManagement = lazy(() => import("./pages/admin/MediaManagement"));
const EventManagement = lazy(() => import("./pages/admin/EventManagement"));
const TournamentManagement = lazy(() => import("./pages/admin/TournamentManagement"));
const ClubRankingsManagement = lazy(() => import("./pages/admin/ClubRankingsManagement"));
const FileManagement = lazy(() => import("./pages/admin/FileManagement"));
const BannerManagement = lazy(() => import("./pages/admin/BannerManagement"));
const Settings = lazy(() => import("./pages/admin/Settings"));
const TrafficAnalytics = lazy(() => import("./pages/admin/TrafficAnalytics"));
const TrainingManagement = lazy(() => import("./pages/admin/TrainingManagement"));
const BlogTrash = lazy(() => import("./pages/admin/BlogTrash"));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
  </div>
);


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <MaintenanceWrapper />
    </AuthProvider>
  </QueryClientProvider>
);

function MaintenanceWrapper() {
  const { isMaintenanceMode, loading } = useMaintenanceMode();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isMaintenanceMode) {
    return <MaintenanceScreen />;
  }

  return (
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Layout><Home /></Layout>} />
            <Route path="/about" element={<Layout><Suspense fallback={<PageLoader />}><About /></Suspense></Layout>} />
            <Route path="/training" element={<Layout><Suspense fallback={<PageLoader />}><Training /></Suspense></Layout>} />
            <Route path="/tournaments" element={<Layout><Suspense fallback={<PageLoader />}><Tournaments /></Suspense></Layout>} />
            <Route path="/events" element={<Layout><Suspense fallback={<PageLoader />}><Events /></Suspense></Layout>} />
            <Route path="/gallery" element={<Layout><Suspense fallback={<PageLoader />}><Gallery /></Suspense></Layout>} />
            <Route path="/search" element={<Layout><Suspense fallback={<PageLoader />}><Search /></Suspense></Layout>} />
            <Route path="/blog" element={<Layout><Suspense fallback={<PageLoader />}><Blog /></Suspense></Layout>} />
            <Route path="/blog/:slug" element={<Layout><Suspense fallback={<PageLoader />}><BlogPost /></Suspense></Layout>} />
            <Route path="/contact" element={<Layout><Suspense fallback={<PageLoader />}><Contact /></Suspense></Layout>} />
            <Route path="/auth" element={<Layout><Suspense fallback={<PageLoader />}><Auth /></Suspense></Layout>} />
            <Route path="/impressum" element={<Layout><Suspense fallback={<PageLoader />}><Impressum /></Suspense></Layout>} />
            <Route path="/datenschutz" element={<Layout><Suspense fallback={<PageLoader />}><Datenschutz /></Suspense></Layout>} />
            <Route path="/agb" element={<Layout><Suspense fallback={<PageLoader />}><AGB /></Suspense></Layout>} />
            <Route path="/unsubscribe" element={<Suspense fallback={<PageLoader />}><Unsubscribe /></Suspense>} />
            
            {/* Admin Routes - All lazy loaded */}
            <Route path="/admin" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><Dashboard /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute requiredRole="admin">
                <AdminLayout><Suspense fallback={<PageLoader />}><UserManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/training" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><TrainingManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/blog" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><BlogManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/blog/trash" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><BlogTrash /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/media" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><MediaManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/events" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><EventManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/tournaments" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><TournamentManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/club-rankings" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><ClubRankingsManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/files" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><FileManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/banner" element={
              <ProtectedRoute requiredRole="admin">
                <AdminLayout><Suspense fallback={<PageLoader />}><BannerManagement /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/settings" element={
              <ProtectedRoute requiredRole="admin">
                <AdminLayout><Suspense fallback={<PageLoader />}><Settings /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/analytics" element={
              <ProtectedRoute requiredRole={["admin", "moderator"]}>
                <AdminLayout><Suspense fallback={<PageLoader />}><TrafficAnalytics /></Suspense></AdminLayout>
              </ProtectedRoute>
            } />
            <Route path="*" element={<Layout><NotFound /></Layout>} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  );
}

export default App;
